import {PhoneNumberTextField} from '@hconnect/uikit'
import {GenericInput, Stepper, StepperItemConfig, TextRegular} from '@hm/fundamental'
import {FormControlLabel, Radio, RadioGroup} from '@mui/material'
import classNames from 'classnames'
import intlTelInput from 'intl-tel-input'
import QRCode from 'qrcode.react'
import React, {useRef} from 'react'

import {useTranslation} from '../../../../hooks/useTranslation'
import {PhoneDataType, TfaType} from '../TfaDialog'
import {useStyles} from '../TfaDialog.styles'

type AddTfaDialogContentProps = {
  activeTab: TfaType
  setActiveTab: (tab: TfaType) => void
  activeStep: number
  steps: string[]
  otpData: {sharedKey: string; qrCodeUri: string}
  isVerifyAppCodeError: boolean
  isVerifyMobileCodeError: boolean
  setCode: (code: string) => void
  isAddPhoneNumberError: boolean
  canSetupAppTfa: boolean
  phoneData: PhoneDataType | undefined
  setPhoneData: (data: PhoneDataType | undefined) => void
  errorMessage?: string
  recoveryCodesData?: string[]
}
// eslint-disable-next-line complexity
export const AddTfaDialogContent = ({
  activeTab,
  setActiveTab,
  activeStep,
  steps,
  otpData,
  isVerifyMobileCodeError,
  isVerifyAppCodeError,
  errorMessage,
  setCode,
  isAddPhoneNumberError,
  recoveryCodesData,
  canSetupAppTfa,
  phoneData,
  setPhoneData
}: AddTfaDialogContentProps) => {
  const {classes} = useStyles()
  const {t} = useTranslation()

  const phoneNumberRef = useRef(null)
  const handlePhoneNumberChange = (
    phoneNumberInput: string,
    countryData?: intlTelInput.CountryData
  ) => {
    const numbersAndPlusOnly = /^[0-9+]*$/
    if (numbersAndPlusOnly.test(phoneNumberInput)) {
      setPhoneData({phoneNumber: phoneNumberInput, countryData})
    }
  }

  const BpIcon = () => <span className={classes.radioIcon} />

  const BpCheckedIcon = () => (
    <span className={classNames(classes.icon, classes.radioCheckedIcon)} />
  )

  const renderTfaRadioButtonGroup = () => {
    return (
      <RadioGroup
        defaultValue={activeTab}
        onChange={(_, value) => setActiveTab(parseInt(value))}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          paddingTop: '36px',
          paddingLeft: '20px',
          gap: '40px'
        }}
      >
        <FormControlLabel
          value={TfaType.APP}
          control={
            <Radio
              classes={{root: classes.radio}}
              data-test-id="tfa-authenticator-radio-button"
              checkedIcon={<BpCheckedIcon />}
              icon={<BpIcon />}
            />
          }
          label={
            <div className="flex flex-col gap-2.5 mt-1.5 ml-3">
              <div className="flex gap-1 flex-wrap">
                <TextRegular>{t('userInformationForm.tfa.add.tabAuthenticator')}</TextRegular>
                <TextRegular className={!canSetupAppTfa ? undefined : 'text-positive'}>
                  {!canSetupAppTfa
                    ? '(' + t('userInformationForm.tfa.add.tabAuthenticatorDisabled') + ')'
                    : '(' + t('userInformationForm.tfa.add.tabAuthenticatoRecommended') + ')'}
                </TextRegular>
              </div>
              <TextRegular>{t('userInformationForm.tfa.add.app.description')}</TextRegular>
            </div>
          }
          style={{alignItems: 'start'}}
          disabled={!canSetupAppTfa}
        />
        <FormControlLabel
          value={TfaType.MOBILE}
          control={
            <Radio
              classes={{root: classes.radio}}
              data-test-id="tfa-mobile-radio-button"
              checkedIcon={<BpCheckedIcon />}
              icon={<BpIcon />}
            />
          }
          label={
            <div className="flex flex-col gap-2.5 mt-1.5 ml-3">
              <TextRegular>{t('userInformationForm.tfa.add.mobile.label')}</TextRegular>
              <TextRegular>{t('userInformationForm.tfa.add.mobile.description')}</TextRegular>
            </div>
          }
          style={{alignItems: 'start'}}
        />
      </RadioGroup>
    )
  }

  const firstStep = (
    <>
      <TextRegular>{t('userInformationForm.tfa.add.title')}</TextRegular>
      <div className="flex flex-col">{renderTfaRadioButtonGroup()}</div>
    </>
  )

  const secondStep = (
    <>
      {activeTab === TfaType.APP && (
        <>
          <TextRegular className="mt-6 mb-[30px]">
            {t('userInformationForm.tfa.add.app.instruction')}
          </TextRegular>
          <div className="flex flex-col items-center justify-center gap-3">
            <QRCode id="qrcode" data-test-id="qrcode" size={150} value={otpData?.qrCodeUri} />
            <TextRegular className="text-center" data-test-id="shared-key">
              {otpData?.sharedKey}
            </TextRegular>
          </div>
        </>
      )}
      {activeTab === TfaType.MOBILE && (
        <>
          <TextRegular className="mt-6">
            {t('userInformationForm.tfa.add.mobile.instruction')}
          </TextRegular>

          <PhoneNumberTextField
            inputRef={phoneNumberRef}
            variant="filled"
            label={t('userInformationForm.tfa.add.mobile.label')}
            data-test-id="mobile-number-input"
            className={classes.mobileNumber}
            value={phoneData?.phoneNumber}
            onPhoneChange={(phoneNumber, _, itiGetCountry) => {
              handlePhoneNumberChange(phoneNumber, itiGetCountry)
            }}
            error={isAddPhoneNumberError}
            helperText={isAddPhoneNumberError && errorMessage}
            readonlyMode={false}
            focused={true}
            InputProps={{disableUnderline: true}}
            InputLabelProps={{focused: false}}
          />
        </>
      )}
    </>
  )

  const thirdStep = (
    <>
      <TextRegular className="mt-6">
        {t(
          `userInformationForm.tfa.add.authCode.${
            activeTab === TfaType.APP ? 'descriptionApp' : 'descriptionMobile'
          }`
        )}
      </TextRegular>
      <GenericInput
        data-test-id="activation-code-input"
        label={t('userInformationForm.tfa.add.authCode.label')}
        className="mt-2"
        onChange={(value) => setCode(value)}
        error={isVerifyAppCodeError || isVerifyMobileCodeError ? errorMessage : undefined}
        isTouched={true}
      />
    </>
  )

  const fourthStep = (
    <div className="flex flex-col gap-6">
      <TextRegular>{t('userInformationForm.tfa.add.success')}</TextRegular>
      {recoveryCodesData && (
        <>
          <TextRegular>{t('userInformationForm.tfa.add.recoveryCodes')}</TextRegular>
          <div className="flex flex-col" data-test-id="recovery-codes-container">
            {recoveryCodesData.map((code) => (
              <TextRegular key={code}>{code}</TextRegular>
            ))}
          </div>
        </>
      )}
    </div>
  )

  const stepItems: StepperItemConfig[] = [
    {
      title: '',
      label: steps[0],
      state: activeStep === 0 ? 'inactive' : activeStep > 0 ? 'completed' : 'inactive',
      content: firstStep
    },
    {
      title: '',
      label: steps[1],
      state: activeStep === 1 ? 'inactive' : activeStep > 1 ? 'completed' : 'inactive',
      content: secondStep
    },
    {
      title: '',
      label: steps[2],
      state: activeStep === 2 ? 'inactive' : activeStep > 2 ? 'completed' : 'inactive',
      content: thirdStep
    },
    {
      title: '',
      label: steps[3],
      state: activeStep === 3 ? 'inactive' : activeStep > 3 ? 'completed' : 'inactive',
      content: fourthStep
    }
  ]

  return <Stepper activeItem={activeStep} items={stepItems} contentClassname="w-full" />
}
